import React, { useMemo, useState } from 'react';
import { CONVERSATION_CREATED_EVENT, CONVERSATION_TITLE_GENERATED_EVENT, useChatWidgetContext } from 'features/nekst-widgets';
import { useDataLoaderNew } from 'data-loader';
import { useEventsSubscriber } from 'event-bus';
import {
  LlmConversation,
  useObjectDatesMapper,
  useLlmConversationsService,
} from 'nekst-api';
import useChatsNavigator from './chatsNavigator';

const PAGE_SIZE = 20;

function getDateGroupLabel(date: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const target = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  if (target.getTime() >= today.getTime()) {
    return 'Today';
  } else if (target.getTime() >= yesterday.getTime()) {
    return 'Yesterday';
  } else if (target.getTime() >= weekAgo.getTime()) {
    return 'Last 7 days';
  } else {
    return 'Older';
  }
}

function getConversationDate(conversation: LlmConversation): Date {
  return conversation.lastMessageAt ?? conversation.createdAt;
}

function getConversationDisplayTitle(conversation: LlmConversation): string {
  if (conversation.title) {
    return conversation.title;
  } else {
    return conversation.createdAt.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
}

function groupConversationsByDate(
  conversations: LlmConversation[],
): { label: string; conversations: LlmConversation[] }[] {
  const groups: Record<string, LlmConversation[]> = {};
  const groupOrder: string[] = [];

  conversations.forEach((conv) => {
    const label = getDateGroupLabel(getConversationDate(conv));
    if (!groups[label]) {
      groups[label] = [];
      groupOrder.push(label);
    }
    groups[label].push(conv);
  });

  return groupOrder.map((label) => ({
    label,
    conversations: groups[label],
  }));
}

function ConversationItem(props: {
  conversation: LlmConversation;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={`block w-full text-left px-3 py-2.5 rounded-lg border-none cursor-pointer text-sm leading-[1.4] text-gray-800 ${
        props.active ? 'bg-gray-200' : 'bg-transparent hover:bg-gray-100'
      }`}
      data-testid="conversation-item"
    >
      <div className="truncate">
        {getConversationDisplayTitle(props.conversation)}
      </div>
      {props.conversation.lastMessagePreview && (
        <div className="text-xs text-gray-500 truncate mt-0.5">
          {props.conversation.lastMessagePreview}
        </div>
      )}
    </button>
  );
}

function DateGroup(props: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-2">
      <div className="text-xs font-semibold text-gray-500 px-3 pt-2 pb-1 uppercase tracking-[0.03em]">
        {props.label}
      </div>
      <div className="flex flex-col gap-0.5">
        {props.children}
      </div>
    </div>
  );
}

function LoadMoreButton(props: { onClick: () => void; loading: boolean }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.loading}
      className={`block w-full px-3 py-2.5 border-none bg-transparent text-gray-500 text-[13px] text-center ${
        props.loading ? 'cursor-default' : 'cursor-pointer'
      }`}
      data-testid="load-more-button"
    >
      {props.loading ? 'Loading...' : 'Load more'}
    </button>
  );
}

export default function ConversationsSidebar() {
  const { resetConversation, currentConversationId } = useChatWidgetContext();
  const conversationsService = useLlmConversationsService();
  const { openChat, openChats } = useChatsNavigator();

  const conversationMapper = useObjectDatesMapper<LlmConversation>([], ['createdAt', 'updatedAt', 'lastMessageAt']);

  const [conversations, setConversations] = useState<LlmConversation[]>([]);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const { loading } = useDataLoaderNew(
    async () => {
      const result = await conversationsService.getConversations({
        limit: PAGE_SIZE,
        offset: 0,
      });
      setConversations(result.data);
      setTotal(result.total);
      return result;
    },
    { dependencies: [] },
  );

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const result = await conversationsService.getConversations({
        limit: PAGE_SIZE,
        offset: conversations.length,
      });
      setConversations((prev) => [...prev, ...result.data]);
      setTotal(result.total);
    } finally {
      setLoadingMore(false);
    }
  };

  useEventsSubscriber(
    'ConversationsSidebar',
    {
      [CONVERSATION_CREATED_EVENT]: (event) => {
        const conversation = conversationMapper.mapSingle(event.conversation as Record<string, unknown>);
        setConversations((prev) => {
          if (prev.some((c) => c.id === conversation.id)) {
            return prev;
          }
          return [conversation, ...prev];
        });
        setTotal((prev) => prev + 1);
      },
      [CONVERSATION_TITLE_GENERATED_EVENT]: (event) => {
        const { conversationId, title } = event as { conversationId: number; title: string };
        setConversations((prev) => prev.map((conv) => {
          if (conv.id === conversationId) {
            return { ...conv, title };
          }
          return conv;
        }));
      },
    },
  );

  const hasMore = conversations.length < total;
  const grouped = useMemo(
    () => groupConversationsByDate(conversations),
    [conversations],
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className="px-3 py-5 text-gray-400 text-sm text-center">
          Loading conversations...
        </div>
      );
    } else if (conversations.length === 0) {
      return (
        <div
          className="px-3 py-5 text-gray-400 text-sm text-center"
          data-testid="empty-conversations"
        >
          No conversations yet
        </div>
      );
    } else {
      return (
        <>
          {grouped.map((group) => (
            <DateGroup key={group.label} label={group.label}>
              {group.conversations.map((conv) => (
                <ConversationItem
                  key={conv.id}
                  conversation={conv}
                  active={currentConversationId === conv.id}
                  onClick={() => openChat(conv.id)}
                />
              ))}
            </DateGroup>
          ))}
          {hasMore && (
            <LoadMoreButton onClick={handleLoadMore} loading={loadingMore} />
          )}
        </>
      );
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-3">
        <button
          type="button"
          className="flex items-center gap-2 w-full px-3 py-2.5 bg-transparent text-gray-800 rounded-lg font-medium text-sm border border-gray-200 cursor-pointer shadow-sm"
          onClick={() => { resetConversation(); openChats(); }}
          data-testid="new-chat-button"
        >
          <span className="text-base leading-none">+</span>
          New Thread
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-3">
        {renderContent()}
      </div>
    </div>
  );
}
